import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { Link } from 'react-router-dom';
import {
  FixedCost,
  ForbiddenError,
  MARKETS,
  MARKET_LABEL,
  Market,
  MarketMonth,
  ROLES,
  ROLE_LABEL,
  RateRow,
  Report,
  Role,
  Settings,
  fetchReport,
  fmt,
  monthLabel,
  saveSetting,
  sumMarketMonths,
} from './api';
import { Waterfall } from './Waterfall';

const SPLIT_KEY = 'profitability.splitFixed';
const PERIOD_KEY = 'profitability.period';

function readPref(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writePref(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private window / blocked storage: the preference just isn't remembered.
  }
}

const mono = { fontFamily: '"Roboto Mono", ui-monospace, Menlo, monospace', fontSize: 12.5 };
const num = { fontVariantNumeric: 'tabular-nums' as const };

const Tag = ({ kind, children }: { kind: 'exp' | 'est' | 'info'; children: React.ReactNode }) => (
  <Chip
    size="small"
    label={children}
    variant={kind === 'est' ? 'outlined' : 'filled'}
    color={kind === 'exp' ? 'warning' : kind === 'info' ? 'info' : 'default'}
    sx={{ height: 18, fontSize: 10.5, ml: 0.5, '& .MuiChip-label': { px: 0.75 } }}
  />
);

/** A dollar input that saves on blur/Enter, only when the value changed. */
const AmountInput = ({
  value,
  onCommit,
  disabled,
  label,
  step = 5,
  placeholder,
}: {
  value: number | null;
  onCommit: (v: number | null) => void;
  disabled?: boolean;
  label: string;
  step?: number;
  placeholder?: string;
}) => {
  const [text, setText] = useState(value === null || value === undefined ? '' : String(value));
  useEffect(() => setText(value === null || value === undefined ? '' : String(value)), [value]);
  const commit = () => {
    const next = text.trim() === '' ? null : Number(text);
    if (next !== null && (!Number.isFinite(next) || next < 0)) {
      setText(value === null ? '' : String(value));
      return;
    }
    if (next !== value) onCommit(next);
  };
  return (
    <TextField
      size="small"
      type="number"
      value={text}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      inputProps={{ 'aria-label': label, min: 0, step, style: { textAlign: 'right', ...mono } }}
      sx={{ width: 110 }}
    />
  );
};

const Panel = ({ title, note, children }: { title: string; note?: React.ReactNode; children: React.ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1.5, alignContent: 'start', minWidth: 0 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
      {title}
    </Typography>
    {note && (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5, maxWidth: '80ch' }}>
        {note}
      </Typography>
    )}
    {children}
  </Paper>
);

const MarketCard = ({
  title,
  z,
  fixedShare,
  total,
}: {
  title: string;
  z: MarketMonth;
  fixedShare: number | null;
  total?: boolean;
}) => {
  const n = z.n || 1;
  const pc = (v: number) => fmt(v / n);
  const netv = z.contribution - (fixedShare || 0);
  const row = (role: Role) => {
    const b = z[role];
    if (!b.billed && !b.expected) return null;
    const pct = b.billed ? `${Math.round((b.expected / (b.billed + b.expected)) * 100)}% ` : '';
    return (
      <TableRow key={role}>
        <TableCell sx={{ pl: 1.25, color: 'text.secondary' }}>
          {ROLE_LABEL[role]}
          {b.expected > 0 && <Tag kind="exp">{pct}expected</Tag>}
          {b.unpaid > 0 && <Tag kind="info">{fmt(b.unpaid)} unpaid</Tag>}
        </TableCell>
        <TableCell align="right">{pc(b.billed + b.expected)}</TableCell>
      </TableRow>
    );
  };
  const line = (label: React.ReactNode, v: number) => (
    <TableRow>
      <TableCell sx={{ pl: 1.25, color: 'text.secondary' }}>{label}</TableCell>
      <TableCell align="right">{pc(v)}</TableCell>
    </TableRow>
  );
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.75, display: 'grid', gap: 1.25, alignContent: 'start', minWidth: 0, borderColor: total ? 'primary.main' : undefined }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={num}>
          {z.n} cases signed
        </Typography>
      </Box>
      {z.n === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No at-need cases signed in this period.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 500, ...num }} color={z.contribution >= 0 ? 'success.main' : 'error.main'}>
                {pc(z.contribution)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                contribution per case
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 500, ...num }}>{fmt(z.contribution)}</Typography>
              <Typography variant="caption" color="text.secondary">
                contribution total
              </Typography>
            </Box>
          </Box>
          <Table
            size="small"
            sx={{
              '& td': { px: 0, py: 0.4, fontSize: 12.5, borderBottom: '1px dashed', borderColor: 'divider', verticalAlign: 'top' },
              '& td:last-of-type': { ...mono, fontSize: 12, whiteSpace: 'nowrap', pl: 0.75 },
              '& tr.sum td': { borderBottom: 0, borderTop: '1px solid', borderTopColor: 'text.primary', fontWeight: 500, pt: 0.75 },
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell>Revenue per case</TableCell>
                <TableCell align="right">{pc(z.net)}</TableCell>
              </TableRow>
              {ROLES.map(row)}
              {line('Government fees', z.gov)}
              {line(
                <>
                  Card fees<Tag kind="est">estimate</Tag>
                </>,
                z.card_fee
              )}
              {line(
                <>
                  Urns and deliveries
                  {!z.delivery && <Tag kind="est">set rate</Tag>}
                </>,
                z.delivery
              )}
              {line('Google Ads per case', z.ads)}
              <TableRow className="sum">
                <TableCell>Contribution</TableCell>
                <TableCell align="right">
                  {pc(z.contribution)} · {z.net ? Math.round((z.contribution / z.net) * 100) : 0}%
                </TableCell>
              </TableRow>
              {fixedShare !== null && (
                <>
                  {line('Fixed costs share', fixedShare)}
                  <TableRow className="sum">
                    <TableCell>Net per case</TableCell>
                    <TableCell align="right" sx={{ color: netv >= 0 ? 'success.main' : 'error.main' }}>
                      {pc(netv)}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </>
      )}
      {z.owed_n > 0 && (
        <Alert severity="warning" icon={false} sx={{ py: 0, fontSize: 12 }}>
          {z.owed_n} signed {z.owed_n > 1 ? 'cases have' : 'case has'} no payment yet: {fmt(z.owed)} owed. Their costs are
          counted, their revenue isn't.
        </Alert>
      )}
    </Paper>
  );
};

const dtSx = {
  '& th': {
    fontSize: 11,
    letterSpacing: '.05em',
    textTransform: 'uppercase',
    color: 'text.secondary',
    fontWeight: 500,
    px: 1,
    whiteSpace: 'nowrap',
  },
  '& td': { px: 1, py: 0.9, verticalAlign: 'top' },
  '& tfoot td': { fontWeight: 500, borderBottom: 0, color: 'text.primary', fontSize: 14 },
};
const Sub = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="caption" color="text.secondary" component="div">
    {children}
  </Typography>
);

export const ProfitabilityPage = () => {
  const notify = useNotify();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>(() => readPref(PERIOD_KEY) || '');
  const [splitFixed, setSplitFixed] = useState<boolean>(() => readPref(SPLIT_KEY) === '1');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetchReport(undefined, 3);
      setReport(r);
      setError(null);
    } catch (e) {
      if (e instanceof ForbiddenError) setForbidden(true);
      else setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async <K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSaving(true);
    try {
      await saveSetting(key, value);
      await load();
    } catch (e) {
      notify(`Could not save: ${(e as Error).message}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const months = useMemo(() => report?.months ?? [], [report]);
  const activePeriod = period && (period === 'all' || months.includes(period)) ? period : months[months.length - 1];
  const keys = activePeriod === 'all' ? months : activePeriod ? [activePeriod] : [];

  const view = useMemo(() => {
    if (!report || !keys.length) return null;
    const byMarket = Object.fromEntries(
      MARKETS.map((mk) => [mk, sumMarketMonths(keys.map((k) => report.markets[k][mk]))])
    ) as Record<Market, MarketMonth>;
    const total = sumMarketMonths(MARKETS.map((mk) => byMarket[mk]));
    const fixedMonthly = report.settings.fixed_costs.reduce((s, f) => s + (Number(f.amount) || 0), 0);
    return { byMarket, total, fixedMonthly, fixed: fixedMonthly * keys.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, keys.join(',')]);

  if (loading)
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  if (forbidden)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">This page is only available to the owner.</Alert>
      </Box>
    );
  if (error || !report || !view)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Could not load profitability: {error || 'no data'}</Alert>
      </Box>
    );

  const { byMarket, total, fixedMonthly, fixed } = view;
  const periodLabel =
    activePeriod === 'all'
      ? `${monthLabel(months[0], 'short')}–${monthLabel(months[months.length - 1])}`
      : monthLabel(activePeriod);
  const editable = report.settings_ready && !saving;
  const settings = report.settings;
  const missing = report.missing_bills.filter((r) => keys.includes(r.month));
  const unpaid = report.unpaid_cases.filter((r) => keys.includes(r.month));
  const adsSources = keys.map((k) => report.ads.source_by_month[k]);
  const needsManualAds = months.filter((m) => ['none', 'manual'].includes(report.ads.source_by_month[m]));
  const adsThroughCurrent = report.ads.through.slice(0, 7) === months[months.length - 1];

  const setRate = (row: RateRow, field: 'rate' | 'held_rate', v: number | null) => {
    const next: Settings['partner_rates'] = JSON.parse(JSON.stringify(settings.partner_rates));
    const cur = { rate: row.rate, held_rate: row.held_rate, ...(next[row.partner_id]?.[row.role] || {}) };
    const updated = { ...cur, [field]: v };
    next[row.partner_id] = { ...(next[row.partner_id] || {}), [row.role]: updated };
    void save('partner_rates', next);
  };
  const resetRate = (row: RateRow) => {
    const next: Settings['partner_rates'] = JSON.parse(JSON.stringify(settings.partner_rates));
    if (next[row.partner_id]) delete next[row.partner_id][row.role];
    void save('partner_rates', next);
  };
  const setFixed = (rows: FixedCost[]) => void save('fixed_costs', rows);

  return (
    <Box sx={{ py: 2.5, px: { xs: 2, md: 3 }, display: 'grid', gap: 2.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Profitability
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: '72ch' }}>
            What each at-need case earns per market after partner bills, government fees, card fees, deliveries and Google
            Ads, and what's left after fixed costs. Pre-need is reported on its own below.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={activePeriod}
            onChange={(_, v) => {
              if (!v) return;
              setPeriod(v);
              writePref(PERIOD_KEY, v);
            }}
            aria-label="Period"
          >
            {months.map((m) => (
              <ToggleButton key={m} value={m} sx={{ px: 1.5 }}>
                {monthLabel(m, 'short')}
              </ToggleButton>
            ))}
            <ToggleButton value="all" sx={{ px: 1.5 }}>
              Last {months.length} months
            </ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={splitFixed}
                onChange={(e) => {
                  setSplitFixed(e.target.checked);
                  writePref(SPLIT_KEY, e.target.checked ? '1' : '0');
                }}
              />
            }
            label={<Typography variant="body2">Split fixed costs by case count</Typography>}
          />
        </Box>
      </Box>

      {!report.settings_ready && (
        <Alert severity="warning">
          The profitability settings table isn't migrated yet, so the default rates are shown and edits can't be saved.
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'center', px: 1.75, py: 1.25, fontSize: 13, color: 'text.secondary' }}
      >
        <span>
          A case belongs to the month it was signed. Revenue is what the family has paid, less refunds, excluding
          GST/QST/HST.
        </span>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="i" sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: 'text.primary', display: 'inline-block' }} />
          Billed by partner
        </Box>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            component="i"
            sx={{
              width: 12,
              height: 12,
              borderRadius: '2px',
              display: 'inline-block',
              border: '1px solid',
              borderColor: 'warning.main',
              background: (t) => `repeating-linear-gradient(45deg, ${t.palette.warning.main} 0 2px, transparent 2px 4px)`,
            }}
          />
          Expected, not billed yet
        </Box>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="i" sx={{ width: 12, height: 12, borderRadius: '2px', border: '1px dashed', borderColor: 'text.secondary', display: 'inline-block' }} />
          Estimate
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gap: 1.75,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))', xl: 'repeat(4, minmax(0,1fr))' },
        }}
      >
        {MARKETS.map((mk) => (
          <MarketCard
            key={mk}
            title={MARKET_LABEL[mk]}
            z={byMarket[mk]}
            fixedShare={splitFixed ? (total.n ? (fixed * byMarket[mk].n) / total.n : 0) : null}
          />
        ))}
        <MarketCard title="All markets" z={total} fixedShare={fixed} total />
      </Box>

      <Panel
        title={`From revenue to net · ${periodLabel}`}
        note="Hatched segments are partner costs we expect but haven't been billed for. Fixed costs come from the table further down."
      >
        <Box sx={{ overflowX: 'auto' }}>
          <Waterfall total={total} fixed={fixed} />
        </Box>
      </Panel>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) minmax(0,1fr)' } }}>
        <Panel
          title="Cases without a partner bill"
          note="These use an expected rate until the invoice is matched on Partner Invoices. Each case is priced on its own: MacKinnon is $669 if the body stayed with them and $275 if it went to a visitation centre; Brampton VC is $175, plus $35 per extra day, $225 for an ID viewing and $150 for a witnessed cremation."
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={dtSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>Market</TableCell>
                  <TableCell align="right">Cases</TableCell>
                  <TableCell align="right">Expected</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {missing.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>
                      Every case in this period has its partner bills.
                    </TableCell>
                  </TableRow>
                )}
                {missing.map((r) => (
                  <TableRow key={`${r.month}-${r.market}-${r.role}-${r.partner_id}`}>
                    <TableCell>{monthLabel(r.month, 'short')}</TableCell>
                    <TableCell>
                      {r.partner_name}
                      {r.no_rate && <Tag kind="est">set rate</Tag>}
                      <Sub>{ROLE_LABEL[r.role]}</Sub>
                    </TableCell>
                    <TableCell>{MARKET_LABEL[r.market]}</TableCell>
                    <TableCell align="right" sx={num}>
                      {r.cases}
                    </TableCell>
                    <TableCell align="right" sx={mono}>
                      {fmt(r.expected)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3}>Total expected</TableCell>
                  <TableCell align="right" sx={num}>
                    {missing.reduce((s, r) => s + r.cases, 0)}
                  </TableCell>
                  <TableCell align="right" sx={mono}>
                    {fmt(missing.reduce((s, r) => s + r.expected, 0))}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </Box>
          {(report.no_partner.transport > 0 || report.no_partner.crematorium > 0) && (
            <Typography variant="caption" color="text.secondary">
              Over the last {months.length} months, {report.no_partner.transport} case(s) have no transport partner and{' '}
              {report.no_partner.crematorium} no crematorium assigned; they carry no expected cost for that role.
            </Typography>
          )}
        </Panel>

        <Panel
          title="Expected rates and per-case costs"
          note='Used only for cases without a matched partner bill. The 90-day column is what matched invoices actually averaged. Rates marked "set rate" count as $0 until you enter one.'
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={dtSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Partner or cost</TableCell>
                  <TableCell align="right">Billed avg, 90d</TableCell>
                  <TableCell align="right">Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.rates.map((r) => (
                  <TableRow key={`${r.partner_id}-${r.role}`}>
                    <TableCell>
                      {r.partner_name}
                      {r.source === 'owner' && <Tag kind="info">your rate</Tag>}
                      {r.rate === null && <Tag kind="est">set rate</Tag>}
                      <Sub>
                        {ROLE_LABEL[r.role]}, per case · {r.note}
                        {r.source === 'owner' && (
                          <>
                            {' · '}
                            <Button size="small" sx={{ p: 0, minWidth: 0, fontSize: 11 }} disabled={!editable} onClick={() => resetRate(r)}>
                              reset to {r.default_rate === null ? 'none' : fmt(r.default_rate)}
                            </Button>
                          </>
                        )}
                      </Sub>
                    </TableCell>
                    <TableCell align="right" sx={mono}>
                      {r.billed_avg_90d === null ? '—' : fmt(r.billed_avg_90d)}
                      {r.billed_cases_90d > 0 && <Sub>{r.billed_cases_90d} cases</Sub>}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'grid', gap: 0.5, justifyItems: 'end' }}>
                        <AmountInput
                          label={`${r.partner_name} ${r.role} rate`}
                          value={r.rate}
                          disabled={!editable}
                          onCommit={(v) => setRate(r, 'rate', v)}
                          placeholder="set rate"
                        />
                        {r.default_held_rate !== null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              held
                            </Typography>
                            <AmountInput
                              label={`${r.partner_name} held rate`}
                              value={r.held_rate}
                              disabled={!editable}
                              onCommit={(v) => setRate(r, 'held_rate', v)}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>
                    Card fees<Tag kind="est">estimate</Tag>
                    <Sub>% of each Stripe payment + a fixed fee per payment; actual fees aren't stored per payment yet</Sub>
                  </TableCell>
                  <TableCell />
                  <TableCell align="right">
                    <Box sx={{ display: 'grid', gap: 0.5, justifyItems: 'end' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          %
                        </Typography>
                        <AmountInput
                          label="Card fee percent"
                          step={0.1}
                          value={settings.card_fee.pct}
                          disabled={!editable}
                          onCommit={(v) => void save('card_fee', { ...settings.card_fee, pct: v ?? 0 })}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          $
                        </Typography>
                        <AmountInput
                          label="Card fee per payment"
                          step={0.05}
                          value={settings.card_fee.fixed}
                          disabled={!editable}
                          onCommit={(v) => void save('card_fee', { ...settings.card_fee, fixed: v ?? 0 })}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    Government fees
                    <Sub>Death certificates (QC), coroner, municipal, BAO (ON) · at cost from contract lines; the family pays the same amount</Sub>
                  </TableCell>
                  <TableCell />
                  <TableCell align="right" sx={mono}>
                    At cost
                  </TableCell>
                </TableRow>
                {(
                  [
                    ['urn', 'Cleo urn, unit cost', 'Urns sold, from contract lines'],
                    ['shipping', 'Canada Post shipment', 'Count from delivery method "shipping"'],
                    ['courier', 'Courrier Plus or hand delivery', 'Count from delivery method "courier" or "hand delivery"'],
                  ] as const
                ).map(([k, label, note]) => (
                  <TableRow key={k}>
                    <TableCell>
                      {label}
                      {!settings.unit_costs[k] && <Tag kind="est">set rate</Tag>}
                      <Sub>{note}</Sub>
                    </TableCell>
                    <TableCell />
                    <TableCell align="right">
                      <AmountInput
                        label={label}
                        value={settings.unit_costs[k]}
                        disabled={!editable}
                        onCommit={(v) => void save('unit_costs', { ...settings.unit_costs, [k]: v ?? 0 })}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) minmax(0,1fr)' } }}>
        <Panel
          title="Fixed costs per month"
          note='Add salaries and overhead here. They always count in the total; tick "Split fixed costs by case count" to also charge each market its share.'
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={dtSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Cost</TableCell>
                  <TableCell align="right">Per month</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {settings.fixed_costs.map((f, i) => (
                  <TableRow key={f.id}>
                    <TableCell>
                      <FixedName
                        value={f.name}
                        disabled={!editable}
                        onCommit={(name) => setFixed(settings.fixed_costs.map((x, j) => (j === i ? { ...x, name } : x)))}
                      />
                      {f.note && <Sub>{f.note}</Sub>}
                    </TableCell>
                    <TableCell align="right">
                      <AmountInput
                        label={`${f.name} per month`}
                        step={50}
                        value={f.amount}
                        disabled={!editable}
                        onCommit={(v) =>
                          setFixed(settings.fixed_costs.map((x, j) => (j === i ? { ...x, amount: v ?? 0 } : x)))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color="inherit"
                        disabled={!editable}
                        onClick={() => setFixed(settings.fixed_costs.filter((_, j) => j !== i))}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell>
                    Total per month
                    {keys.length > 1 ? ` · ${fmt(fixed)} for ${keys.length} months` : ''}
                  </TableCell>
                  <TableCell align="right" sx={mono}>
                    {fmt(fixedMonthly)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </Box>
          <Box>
            <Button
              variant="outlined"
              size="small"
              disabled={!editable}
              onClick={() =>
                setFixed([...settings.fixed_costs, { id: `c${Date.now()}`, name: 'New cost', amount: 0 }])
              }
            >
              Add cost
            </Button>
          </Box>
        </Panel>

        <Panel
          title="Pre-need, kept separate"
          note="Signed pre-need contracts by market. Pre-need is funded through TruStage, so there is no Stripe revenue to show until we decide what counts (commission, or the contract at time of death)."
        >
          <Box sx={{ overflowX: 'auto' }}>
            <PreneedTable report={report} keys={keys} />
          </Box>
          <Alert severity="info" icon={false} sx={{ fontSize: 12.5 }}>
            Revenue basis for pre-need: to be decided. Not included in any total above.
          </Alert>
        </Panel>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) minmax(0,1fr)' } }}>
        <Panel
          title="Signed, not paid"
          note="At-need cases signed in this period with no payment. Their partner and government costs are in the figures above; their revenue is $0 until they pay."
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={dtSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell>Case</TableCell>
                  <TableCell>Market</TableCell>
                  <TableCell align="right">Owed</TableCell>
                  <TableCell align="right">Costs counted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unpaid.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>
                      Every case signed in this period has paid.
                    </TableCell>
                  </TableRow>
                )}
                {unpaid.map((u) => (
                  <TableRow key={u.funeral_id}>
                    <TableCell>{monthLabel(u.month, 'short')}</TableCell>
                    <TableCell>
                      <Link to={`/funerals/${u.funeral_id}/show`}>{u.name}</Link>
                    </TableCell>
                    <TableCell>{MARKET_LABEL[u.market]}</TableCell>
                    <TableCell align="right" sx={mono}>
                      {fmt(u.owed)}
                    </TableCell>
                    <TableCell align="right" sx={mono}>
                      {fmt(u.costs)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {unpaid.length > 0 && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={3}>{unpaid.length} cases</TableCell>
                    <TableCell align="right" sx={mono}>
                      {fmt(unpaid.reduce((s, u) => s + u.owed, 0))}
                    </TableCell>
                    <TableCell align="right" sx={mono}>
                      {fmt(unpaid.reduce((s, u) => s + u.costs, 0))}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </Box>
        </Panel>

        <Panel
          title="Google Ads spend"
          note="French + French brand go to Montreal FR, English QC + English brand to Montreal EN, Toronto + Ontario PMax to Toronto."
        >
          <Typography variant="body2">
            {adsSources.every((s) => s === 'api') ? (
              <>
                From the Google Ads API
                {adsThroughCurrent ? ` through ${report.ads.through}` : ''}.
              </>
            ) : (
              <>
                Source:{' '}
                {keys
                  .map(
                    (k) =>
                      `${monthLabel(k, 'short')} ${
                        { api: 'Ads API', ads_daily: 'nightly ads_daily table', manual: 'your figures', none: 'none' }[
                          report.ads.source_by_month[k]
                        ]
                      }`
                  )
                  .join(' · ')}
              </>
            )}
          </Typography>
          {report.ads.error && (
            <Alert severity="warning" sx={{ fontSize: 12.5 }}>
              Google Ads API unavailable: {report.ads.error}
            </Alert>
          )}
          {report.ads.unmapped.length > 0 && (
            <Alert severity="warning" sx={{ fontSize: 12.5 }}>
              Not assigned to a market (left out of the cards):{' '}
              {report.ads.unmapped.map((u) => `${u.campaign} ${monthLabel(u.month, 'short')} ${fmt(u.cost)}`).join(', ')}
            </Alert>
          )}
          <Table size="small" sx={dtSx}>
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                {MARKETS.map((mk) => (
                  <TableCell key={mk} align="right">
                    {MARKET_LABEL[mk]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {months.map((m) => (
                <TableRow key={m}>
                  <TableCell>{monthLabel(m, 'short')}</TableCell>
                  {MARKETS.map((mk) => (
                    <TableCell key={mk} align="right" sx={mono}>
                      {needsManualAds.includes(m) ? (
                        <AmountInput
                          label={`Google Ads ${MARKET_LABEL[mk]} ${m}`}
                          step={50}
                          value={settings.ads_manual[m]?.[mk] ?? null}
                          disabled={!editable}
                          onCommit={(v) => {
                            const next = JSON.parse(JSON.stringify(settings.ads_manual));
                            next[m] = { ...(next[m] || {}), [mk]: v ?? 0 };
                            void save('ads_manual', next);
                          }}
                        />
                      ) : (
                        fmt(report.markets[m][mk].ads)
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: '110ch' }}>
        Sources: <b>revenue, tax, government fees</b> from Stripe and e-transfer payments and contract lines ·{' '}
        <b>partner costs</b> from invoices matched on Partner Invoices (before tax), else the case's assigned partner
        times the expected rate · <b>card fees</b> estimated per Stripe payment · <b>market</b> = Ontario cases are
        Toronto; Quebec cases split by the case's language. Generated{' '}
        {new Date(report.generated_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}.
      </Typography>
    </Box>
  );
};

const FixedName = ({
  value,
  onCommit,
  disabled,
}: {
  value: string;
  onCommit: (v: string) => void;
  disabled?: boolean;
}) => {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <TextField
      size="small"
      fullWidth
      value={text}
      disabled={disabled}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text.trim() && text !== value) onCommit(text.trim());
        else setText(value);
      }}
      inputProps={{ 'aria-label': 'Cost name' }}
    />
  );
};

const PreneedTable = ({ report, keys }: { report: Report; keys: string[] }) => {
  const rows = MARKETS.map((mk) => {
    let n = 0;
    let value = 0;
    let collected = 0;
    for (const k of keys) {
      const c = report.preneed[k]?.[mk];
      if (!c) continue;
      n += c.n;
      value += c.value;
      collected += c.collected;
    }
    return { mk, n, value, collected };
  });
  const t = rows.reduce((a, r) => ({ n: a.n + r.n, value: a.value + r.value, collected: a.collected + r.collected }), {
    n: 0,
    value: 0,
    collected: 0,
  });
  return (
    <Table size="small" sx={dtSx}>
      <TableHead>
        <TableRow>
          <TableCell>Market</TableCell>
          <TableCell align="right">Signed</TableCell>
          <TableCell align="right">Contract value incl. tax</TableCell>
          <TableCell align="right">Collected</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.mk}>
            <TableCell>{MARKET_LABEL[r.mk]}</TableCell>
            <TableCell align="right" sx={num}>
              {r.n}
            </TableCell>
            <TableCell align="right" sx={mono}>
              {fmt(r.value)}
            </TableCell>
            <TableCell align="right" sx={mono}>
              {fmt(r.collected)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell>Total</TableCell>
          <TableCell align="right" sx={num}>
            {t.n}
          </TableCell>
          <TableCell align="right" sx={mono}>
            {fmt(t.value)}
          </TableCell>
          <TableCell align="right" sx={mono}>
            {fmt(t.collected)}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
};

export default ProfitabilityPage;
